import {Component, EventEmitter, Input, Output} from '@angular/core';
import {TreeNode} from '../../../../../content/shared/interfaces/tree-node.interface';

/**
 * Class for the BaseTreeNodeComponent
 * @class BaseTreeNodeComponent
 *
 *
 * @description Basic tree node implementation. Also serves as a base for any other custom tree node types
 *
 *
 * @usage
 *
 * ```html
 *
 * <advtech-base-tree-node
 *  [node]='node'
 *  [isChild]='true'
 *  [value]='value'
 *  (onTreeNodeSelection)='hTreeNodeSelection($event, node)'
 * ></advtech-base-tree-node>
 * ```
 *
 * # Inputs
 *
 * |name|type|description|
 * |---|---|---|
 * |node|[[TreeNode]]|The node value for this tree node|
 * |isChild|boolean|Is this a child of another node|
 * |value|[[TreeNode]][]|The current value for the input|
 *
 * # Outputs
 *
 * |name|type|description
 * |---|---|---|
 * |onTreeNodeSelection|[EventEmitter]{@link https://angular.io/api/core/EventEmitter}<{value}>|Emits the selected node
 */
@Component({
  selector: 'advtech-base-tree-node',
  templateUrl: './base-tree-node.component.html',
})
export class BaseTreeNodeComponent {
  /**
   * The node value we are working with
   * @type {TreeNode}
   */
  @Input()
  node: TreeNode;

  /**
   * Flag forwhether this node is a child of another one
   * @type {boolean}
   */
  @Input()
  isChild: boolean = false;

  /**
   * Current value for the input
   * @type {Array}
   */
  @Input()
  value: TreeNode[] = [];

  /**
   * Handles tree node selection event
   * @type {EventEmitter<any>}
   */
  @Output()
  onTreeNodeSelection: EventEmitter<{ value }> = new EventEmitter();

  /**
   * Using {@link https://www.typescriptlang.org/docs/handbook/advanced-types.html | type guards} to check if the value is a TreeNode
   * @param {TreeNode | any} arg
   * @returns {boolean}
   */
  isTreeNode(arg: TreeNode | any): arg is TreeNode {
    return arg.parentLabel !== undefined;
  }

  /**
   * Emits the tree node selection
   * @param {any} e Value from the ionic input
   * @param {TreeNode} node The tree node
   * @returns {void}
   */
  hTreeNodeSelection(e: any, node: TreeNode): void {
    const value = e.value && this.isTreeNode(e.value) ? e.value : node;
    this.onTreeNodeSelection.emit({value});
  }

  /**
   * Checks to see if the value is selected
   * @param {TreeNode} node The tree node
   * @returns {boolean}
   */
  isSelected(node: TreeNode): boolean {
    let checked = false;
    if (this.value) {
      for (const val of this.value) {
        if (val.value === node.value) {
          checked = true;
        }
      }
    }
    return checked;
  }
}


// WEBPACK FOOTER //
// ./src/app/shared/components/tree/tree-node/base-tree-node/base-tree-node.component.ts